/**
 * IdleSessionGate — the 30-minute sign-out, wired into Console.
 *
 * Sits inside ProtectedRoute, so it only ever runs for somebody who is signed
 * in. It does three things:
 *
 *   1. Shows the two-minute warning at 28 minutes.
 *   2. Replaces the whole app with the lock screen at 30 minutes.
 *   3. In one tab only, records the sign-out and revokes the session.
 *
 * The app is unmounted rather than blurred. A blur removes nothing: the text is
 * still in the page and anybody with the developer tools reads it in full. With
 * the tree gone there is nothing behind the lock screen to read.
 *
 * Console is the account and identity screen, so this is also where the shared
 * clock is started for the whole family of tabs.
 */

import React, { useCallback } from 'react';
import { useIdleSession, IdleLockScreen, IdleWarningBanner } from '@cloudsam/ui';
import { useAuth } from '../context/AuthContext';
import { idleClock, IDLE_LOCK_MINUTES } from '../idle-session';
import { closeHelpAgent } from '../utils/openHelpAgent';
import hanko from '../hanko-init';
import SamLogo from './SamLogo';

/**
 * Tell the server the session is ending, while the token is still valid.
 *
 * Hanko does not report a sign-out to CloudSAM. Without this call the stored
 * expiry stays in the future and the SuperPanel Monitor keeps listing this
 * person as signed in, for up to a day. Never let it block the sign-out.
 */
async function recordSignOut() {
    try {
        await fetch('/api/console/signout', { method: 'POST', credentials: 'include' });
    } catch (err) {
        console.warn('[IdleSessionGate] idle sign-out could not be recorded');
    }
}

export default function IdleSessionGate({ children }) {
    const { isAuthenticated } = useAuth();

    const handleLock = useCallback(async ({ owned }) => {
        closeHelpAgent();
        // Only the tab that claimed the sign-out talks to the server. The
        // others have already locked their screens and have nothing to do.
        if (!owned) return;
        // Order matters. Record first, while the token still works, then
        // revoke. Any draft save has already finished by the time this runs —
        // useIdleSession waits for it — because revoking mid-save would make
        // the save fail with a 401.
        await recordSignOut();
        try {
            await hanko.user.logout();
        } catch (err) {
            // The screen is locked either way, and the server-side idle check
            // is the backstop for a revoke that did not land.
            console.warn('[IdleSessionGate] session revoke failed');
        }
    }, []);

    const idle = useIdleSession({
        clock: idleClock,
        enabled: Boolean(isAuthenticated),
        onLock: handleLock,
    });

    if (idle.isLocked) {
        return (
            <IdleLockScreen
                logo={<SamLogo size={56} showWordmark />}
                appName="Console"
                reason={idle.reason}
                saving={idle.saving}
                saveResults={idle.saveResults}
                idleMinutes={IDLE_LOCK_MINUTES}
                // Back to Console's own front door. ProtectedRoute shows the
                // sign-in form there once the session is gone.
                signInUrl={typeof window !== 'undefined' ? window.location.origin + '/' : '/'}
            />
        );
    }

    return (
        <>
            {children}
            {idle.isWarning && (
                <IdleWarningBanner
                    secondsUntilLock={idle.secondsUntilLock}
                    saving={idle.saving}
                    onStaySignedIn={idle.staySignedIn}
                />
            )}
        </>
    );
}
